<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <!-- Status Widgets -->
    <StatusWidgets :systemStatus="systemStatus" />
    
    <!-- Actions and Info -->
    <div class="row mt-4">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">
              <i class="bx bx-info-circle me-2"></i>Informações do Sistema
            </h4>
            <div class="table-responsive">
              <table class="table table-nowrap">
                <tbody>
                  <tr>
                    <td><strong>Serviço</strong></td>
                    <td>{{ systemStatus.service || 'RadioConnect API' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Status Geral</strong></td>
                    <td>
                      <span :class="getOverallStatusBadge()">{{ systemStatus.status || 'OK' }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Versão</strong></td>
                    <td>{{ systemStatus.version || '1.0.0' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Última Verificação</strong></td>
                    <td>{{ new Date().toLocaleString('pt-BR') }}</td>
                  </tr>
                  <tr>
                    <td><strong>Uptime da Sessão</strong></td>
                    <td>{{ calculateUptime() }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">
              <i class="bx bx-cog me-2"></i>Ações
            </h4>
            
            <div class="d-grid gap-2">
              <button 
                class="btn btn-primary"
                @click="refreshAllStatus"
                :disabled="loading"
              >
                <i v-if="loading" class="bx bx-loader bx-spin me-2"></i>
                <i v-else class="bx bx-refresh me-2"></i>
                {{ loading ? 'Verificando...' : 'Atualizar Tudo' }}
              </button>
              
              <button 
                class="btn btn-outline-secondary ml-3"
                @click="refreshSystemStatus"
                :disabled="loadingStatus"
              >
                <i v-if="loadingStatus" class="bx bx-loader bx-spin me-2"></i>
                <i v-else class="bx bx-server me-2"></i>
                Status dos Sistemas
              </button>
            </div>

            <hr>

            <h6 class="mb-3">Status Rápido</h6>
            <div class="d-flex flex-column gap-2">
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-size-13">API</span>
                <span :class="getQuickStatusClass(systemStatus.status)">
                  {{ systemStatus.status || 'OK' }}
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-size-13">Orthanc</span>
                <span :class="getQuickStatusClass(systemStatus.systems?.orthanc_status)">
                  {{ systemStatus.systems?.orthanc_status || '---' }}
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <span class="font-size-13">Database</span>
                <span :class="getQuickStatusClass(systemStatus.systems?.database_status)">
                  {{ systemStatus.systems?.database_status || '---' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import StatusWidgets from "./status-widgets.vue";
import { adminService } from "@/api/admin";

export default {
  page: {
    title: "Status do Sistema",
    meta: [{ name: "description", content: "Status e informações completas do sistema" }],
  },
  components: {
    Layout,
    PageHeader,
    StatusWidgets,
  },
  data() {
    return {
      title: "Status do Sistema",
      items: [
        {
          text: "Administração",
          href: "/admin",
        },
        {
          text: "Status",
          active: true,
        },
      ],
      systemStatus: {},
      loading: false,
      loadingStatus: false,
      startTime: new Date(),
      refreshInterval: null,
    };
  },
  async created() {
    await this.loadInitialData();
    this.setupAutoRefresh();
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async loadInitialData() {
      await this.loadSystemStatus();
    },

    async loadSystemStatus() {
      this.loadingStatus = true;
      try {
        const response = await adminService.getSystemOverview();
        this.systemStatus = response.data;
      } catch (error) {
        console.error("Erro ao carregar status do sistema:", error);
        this.$toast.error("Erro ao carregar status do sistema");
        // Fallback para API básica
        try {
          const fallbackResponse = await adminService.getSystemStatus();
          this.systemStatus = fallbackResponse.data;
        } catch (fallbackError) {
          console.error("Erro no fallback:", fallbackError);
        }
      } finally {
        this.loadingStatus = false;
      }
    },

    async loadMetrics() {
      // Removido - não utilizamos mais métricas detalhadas
    },

    async refreshSystemStatus() {
      await this.loadSystemStatus();
      this.$toast.success("Status dos sistemas atualizado!");
    },

    async refreshMetrics() {
      // Removido - não utilizamos mais métricas detalhadas
    },

    async refreshAllStatus() {
      this.loading = true;
      try {
        await this.loadSystemStatus();
        this.$toast.success("Status atualizado!");
      } catch (error) {
        console.error("Erro ao atualizar informações:", error);
        this.$toast.error("Erro ao atualizar informações");
      } finally {
        this.loading = false;
      }
    },

    setupAutoRefresh() {
      // Auto-refresh a cada 30 segundos
      this.refreshInterval = setInterval(() => {
        this.loadSystemStatus();
      }, 30000);
    },

    calculateUptime() {
      const now = new Date();
      const diffTime = Math.abs(now - this.startTime);
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
      
      return `${diffHours}h ${diffMinutes}m`;
    },

    formatPeriod() {
      // Removido - não utilizamos mais métricas com período
      return '';
    },

    getOverallStatusBadge() {
      const status = this.systemStatus.status;
      switch(status) {
        case 'OK': return 'badge bg-success';
        case 'WARNING': return 'badge bg-warning';
        case 'ERROR': return 'badge bg-danger';
        default: return 'badge bg-secondary';
      }
    },

    getQuickStatusClass(status) {
      switch(status) {
        case 'OK':
        case 'online': 
          return 'badge bg-soft-success text-success font-size-11';
        case 'offline': 
          return 'badge bg-soft-danger text-danger font-size-11';
        case 'warning': 
          return 'badge bg-soft-warning text-warning font-size-11';
        default: 
          return 'badge bg-soft-secondary text-muted font-size-11';
      }
    },
  },
};
</script>
